import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './properties.css';

function Properties() {
  const [properties, setProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // ✅ Collapsible archived section (default closed)
  const [archivedOpen, setArchivedOpen] = useState(() => {
    const saved = localStorage.getItem('properties_archived_open');
    return saved ? saved === '1' : false;
  });

  // Fetch properties from the backend using Axios and sort by Property ID
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get('/api/properties');
        const sortedProperties = (response.data || []).sort((a, b) => a.id - b.id);
        setProperties(sortedProperties);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    fetchProperties();
  }, []);

  // Format property ID as #000007
  const formatPropertyId = (id) => `#${String(id).padStart(5, '0')}`;

  const filteredProperties = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return Array.isArray(properties)
      ? properties.filter((property) => {
          const clientDisplayName = property.client_display_name || '';
          const buildingName = property.address_line_1 || '';
          const unitNumber = property.unit_number || '';
          const propertyIdFormatted = formatPropertyId(property.id);
          const propertyId = property.id ? String(property.id) : '';

          return (
            clientDisplayName.toLowerCase().includes(term) ||
            buildingName.toLowerCase().includes(term) ||
            unitNumber.toLowerCase().includes(term) ||
            propertyId.includes(searchTerm) || // keep numeric behavior
            propertyIdFormatted.toLowerCase().includes(`#${searchTerm.padStart(5, '0')}`.toLowerCase())
          );
        })
      : [];
  }, [properties, searchTerm]);

  const activeProperties = useMemo(
    () => filteredProperties.filter((property) => property.is_active !== false),
    [filteredProperties]
  );

  const archivedProperties = useMemo(
    () => filteredProperties.filter((property) => property.is_active === false),
    [filteredProperties]
  );

  const handleRowClick = (propertyId) => {
    navigate(`/properties/${propertyId}`);
  };

  const toggleArchived = () => {
    setArchivedOpen((prev) => {
      const next = !prev;
      localStorage.setItem('properties_archived_open', next ? '1' : '0');
      return next;
    });
  };

  return (
    <div className="properties-container">
      <h2>Properties</h2>

      <div className="search-and-add-container">
        <div className="search-bar-wrapper">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search by Client Name, Building Name, Unit Number, or Property ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-bar"
          />
        </div>
        <Link to={`/properties/new/`}>
          <button className="add-property-button">+ Add New Property</button>
        </Link>
      </div>

      <table className="properties-table">
        <thead>
          <tr>
            <th>Property ID</th>
            <th>Client Display Name</th>
            <th>Building Name</th>
            <th style={{ textAlign: 'center' }}>Unit Number</th>
            <th style={{ textAlign: 'center' }}>Bedrooms</th>
            <th style={{ textAlign: 'center' }}>Area</th>
          </tr>
        </thead>
        <tbody>
          {activeProperties.map((property) => (
            <tr
              key={property.id}
              onClick={() => handleRowClick(property.id)}
              className="property-row"
              style={{ cursor: 'pointer' }}
            >
              <td>{formatPropertyId(property.id)}</td>
              <td>{property.client_display_name}</td>
              <td>{property.address_line_1}</td>
              <td style={{ textAlign: 'center' }}>{property.unit_number}</td>
              <td style={{ textAlign: 'center' }}>{property.bedrooms}</td>
              <td style={{ textAlign: 'center' }}>{property.area}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ✅ Collapsible Archived Properties */}
      {archivedProperties.length > 0 && (
        <div className="archived-properties-section">
          <div
            className="archived-properties-header"
            onClick={toggleArchived}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleArchived()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none',
              marginTop: 18,
            }}
          >
            <h3 style={{ margin: 0 }}>Archived Properties ({archivedProperties.length})</h3>
            <FontAwesomeIcon icon={archivedOpen ? faChevronDown : faChevronRight} />
          </div>

          {archivedOpen && (
            <table className="properties-table archived-properties-table" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Property ID</th>
                  <th>Client Display Name</th>
                  <th>Building Name</th>
                  <th style={{ textAlign: 'center' }}>Unit Number</th>
                  <th style={{ textAlign: 'center' }}>Bedrooms</th>
                  <th style={{ textAlign: 'center' }}>Area</th>
                </tr>
              </thead>
              <tbody>
                {archivedProperties.map((property) => (
                  <tr
                    key={property.id}
                    onClick={() => handleRowClick(property.id)}
                    className="property-row archived"
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{formatPropertyId(property.id)}</td>
                    <td>{property.client_display_name}</td>
                    <td>{property.address_line_1}</td>
                    <td style={{ textAlign: 'center' }}>{property.unit_number}</td>
                    <td style={{ textAlign: 'center' }}>{property.bedrooms}</td>
                    <td style={{ textAlign: 'center' }}>{property.area}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default Properties;
